import { ReactNode } from 'react';
import Term from './Term';
import { GLOSSARY, type GlossaryKey } from '../../lib/glossary';

type Tone = 'default' | 'navy' | 'accent' | 'warning' | 'danger';

interface Props {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  tone?: Tone;
  /** Glossary key or inline text explained on hover/focus of the label. */
  help?: GlossaryKey | string;
  /** `tile` is the Exam `.tile`: a 17px display-font value (names, dates) instead of a big mono number. */
  variant?: 'stat' | 'tile';
}

const WRAP: Record<Tone, string> = {
  default: 'border-hairline bg-panel text-ink',
  navy: 'border-navy bg-navy text-white',
  accent: 'border-accent-line bg-accent-bg text-ink',
  warning: 'border-transparent bg-warning-bg text-warning-ink',
  danger: 'border-transparent bg-danger-bg text-danger-ink',
};

const LABEL: Record<Tone, string> = {
  default: 'text-muted',
  navy: 'text-on-navy-muted',
  accent: 'text-muted',
  warning: 'text-warning-ink',
  danger: 'text-danger-ink',
};

/** Design `.stats`: tiles in an auto-fit grid, each at least 132px wide, 12px apart. */
export function StatGrid({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`grid grid-cols-[repeat(auto-fit,minmax(132px,1fr))] gap-3 ${className}`}>{children}</div>;
}

const SUB: Record<Tone, string> = {
  default: 'text-muted',
  navy: 'text-on-navy-muted',
  accent: 'text-muted',
  warning: 'text-warning-ink',
  danger: 'text-danger-ink',
};

/** Design `.stat` (CP): #F5F8FA tile, hairline border, 14/16 padding, 26px mono value. */
export default function StatTile({ label, value, sub, tone = 'default', help, variant = 'stat' }: Props) {
  const tile = variant === 'tile';
  return (
    <div className={`min-w-0 rounded-[14px] border ${tile ? 'p-3.5' : 'px-4 py-3.5'} ${tile && tone === 'default' ? 'border-track bg-panel text-ink' : WRAP[tone]}`}>
      <p className={`font-mono text-[11px] font-semibold uppercase tracking-[0.08em] ${LABEL[tone]}`}>
        {help ? <Term {...(help in GLOSSARY ? { k: help as GlossaryKey } : { def: help })}>{label}</Term> : label}
      </p>
      {tile
        ? <p className="mt-1.5 font-display text-[17px] font-bold">{value}</p>
        : <p className="mt-2 font-mono text-[26px] font-semibold leading-[1.1] tracking-[-0.03em]">{value}</p>}
      {sub && <div className={`mt-1 text-xs ${SUB[tone]}`}>{sub}</div>}
    </div>
  );
}
